import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "ml-dl",
  name: "ML/DL応用",
  nameEn: "ML & Deep Learning",
  seo: {
    title:
      "ML/DL応用を学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "scikit-learn・TensorFlow・PyTorchからCNN・Transformerまで、機械学習・深層学習の応用スキルをゆめさくエンジニアで実践的に学びましょう。AI分野で最も高い技術力が求められる領域です。",
    url: "/columns/ml-dl-programming-school",
  },
  hero: {
    title: "ML/DL応用を学べる女性向けプログラミングスクール",
    englishTitle: "Learn ML & Deep Learning",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "ML/DL応用" },
  ],
  overview: {
    heading: "ML/DL応用とは？",
    paragraphs: [
      "ML（Machine Learning：機械学習）とDL（Deep Learning：深層学習）の応用とは、scikit-learnやTensorFlow・PyTorchなどのフレームワークを使い、実践的な機械学習・深層学習モデルを構築・評価・デプロイするスキル領域です。",
      "教師あり・教師なし学習の基礎から、CNN（畳み込みニューラルネットワーク）やTransformerなどの最新アーキテクチャまで、AI技術の最前線を学びます。画像認識、自然言語処理、時系列予測など、幅広い問題に対応できる実践力を養います。",
      "AI・機械学習の基礎を一歩超え、実際のビジネス課題やプロダクトに応用できる高度なスキルを身につけることで、MLエンジニア・データサイエンティストとしてのキャリアが開けます。",
    ],
    useCases: [
      {
        label: "画像認識・コンピュータビジョン",
        description:
          "CNNを使った画像分類、物体検出、セグメンテーションなど、画像データを扱うAIモデルを構築します。",
      },
      {
        label: "自然言語処理（NLP）",
        description:
          "Transformerベースのモデルを使い、テキスト分類、感情分析、文書生成、質問応答システムなどを開発します。",
      },
      {
        label: "時系列予測",
        description:
          "LSTMやTransformerを活用して、株価予測、需要予測、異常検知などの時系列データ分析を行います。",
      },
      {
        label: "モデル評価・MLOps",
        description:
          "精度・再現率・F値などの評価指標を使ったモデル評価、ハイパーパラメータチューニング、本番環境へのデプロイを行います。",
      },
    ],
  },
  whyLearn: {
    heading: "ML/DL応用を学ぶべき3つの理由",
    items: [
      {
        title: "AI分野で最も高い報酬が得られるスキル",
        description:
          "MLエンジニア・データサイエンティストはIT業界の中でもトップクラスの年収水準を誇ります。特に深層学習の実践スキルを持つ人材は希少であり、非常に高い市場価値があります。",
      },
      {
        title: "AI技術の急速な進化に対応できる基盤力",
        description:
          "ML/DLの原理を深く理解していれば、GPTやStable Diffusionなど新しいモデルが登場しても迅速にキャッチアップできます。表面的なツールの使い方だけでなく、本質的な理解が得られます。",
      },
      {
        title: "研究からプロダクト開発まで幅広いキャリアパス",
        description:
          "ML/DLスキルは、AI研究、プロダクト開発、コンサルティング、スタートアップなど、非常に多様なキャリアの選択肢を提供します。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでML/DL応用を学ぶ",
    paragraphs: [
      "ゆめさくエンジニアでは、Python・データ分析の基礎を習得した後、上級PhaseでML/DLの応用スキルを本格的に学びます。数式の暗記ではなく、実際にモデルを構築しながら理論を理解するアプローチを採用しています。",
      "Google Colabを活用したGPU環境での学習や、Kaggleデータセットを使った実践的なプロジェクトを通じて、ポートフォリオに載せられる実績を作ります。",
    ],
    topics: [
      "scikit-learnによる機械学習モデル構築と評価",
      "ニューラルネットワークの基礎と実装",
      "TensorFlow/PyTorchによる深層学習",
      "CNN（畳み込みニューラルネットワーク）",
      "Transformerアーキテクチャの理解と活用",
      "モデル評価・ハイパーパラメータチューニング",
      "実践プロジェクト：深層学習モデルの構築とデプロイ",
    ],
  },
  careerPaths: {
    heading: "ML/DL応用を活かせるキャリア",
    items: [
      {
        title: "MLエンジニア",
        description:
          "機械学習・深層学習モデルの設計・開発・本番環境へのデプロイを担当します。データパイプラインの構築からモデルの運用・改善まで、AI技術を実プロダクトに組み込む中核的な役割です。",
        salary: "600〜1,100万円",
      },
      {
        title: "データサイエンティスト",
        description:
          "ビジネス課題をデータ分析と機械学習で解決する職種です。統計学・ML/DLの高度な知識とビジネス理解を兼ね備え、データに基づいた意思決定を推進します。",
        salary: "550〜1,000万円",
      },
      {
        title: "AI研究",
        description:
          "新しいアルゴリズムやアーキテクチャの研究開発を行う職種です。大学・研究機関・企業の研究部門で、AI技術の最前線を切り拓きます。",
        salary: "700〜1,300万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "12〜24ヶ月（数学+Python+ML基礎+DL応用）",
        luagate: "6〜8ヶ月（基礎から応用まで最短ルートで到達）",
      },
      {
        label: "挫折率",
        selfStudy: "線形代数・微積分の壁で9割以上が挫折",
        luagate: "必要な数学はコードと一緒に学び、実感で理解",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "チュートリアルのコピーで終わりがち",
        luagate: "Kaggleデータセットを使った本格的なモデル構築",
      },
      {
        label: "質問対応",
        selfStudy: "モデルの精度改善やバグ解消に数日かかる",
        luagate: "ML経験豊富なメンターが最適な手法を提案",
      },
      {
        label: "就職サポート",
        selfStudy: "ML/AI職の未経験求人が見つけにくい",
        luagate: "AI企業との接点を活かした就職・転職サポート",
      },
    ],
  },
  faq: [
    {
      question: "ML/DL応用を学ぶには高い数学力が必要ですか？",
      answer:
        "大学レベルの線形代数・微積分・確率統計の基礎があると理解が深まりますが、ゆめさくエンジニアでは必要な数学をコードの実装と並行して学ぶため、数学に自信がない方でも段階的に習得できます。",
    },
    {
      question: "AI・機械学習コースとML/DL応用コースの違いは何ですか？",
      answer:
        "AI・機械学習コースはAI全般の基礎（データ分析、基本的なML、プロンプトエンジニアリング等）を幅広く学ぶコースです。ML/DL応用コースは、深層学習（CNN・Transformer等）やモデルの評価・デプロイなど、より高度で専門的なスキルに特化しています。",
    },
    {
      question: "GPU搭載のPCが必要ですか？",
      answer:
        "学習段階ではGoogle ColabでGPUを無料利用できるため、一般的なPCで問題ありません。ゆめさくエンジニアではクラウドGPU環境の活用方法も指導しています。",
    },
    {
      question: "ML/DLの知識はLLM時代にも役立ちますか？",
      answer:
        "非常に役立ちます。ChatGPTのようなLLMもTransformerアーキテクチャをベースにしており、ML/DLの基礎知識があればLLMの仕組みを深く理解し、ファインチューニングやRAG構築などの高度な活用ができるようになります。",
    },
    {
      question: "データサイエンティストとMLエンジニアの違いは何ですか？",
      answer:
        "データサイエンティストはビジネス課題の分析・仮説検証・モデル構築が主な役割で、MLエンジニアはモデルの本番環境へのデプロイ・運用・スケーリングに重点を置きます。ゆめさくエンジニアでは両方のキャリアに対応したスキルを学べます。",
    },
  ],
  relatedLanguages: [
    { slug: "python", name: "Python" },
    { slug: "ai-ml", name: "AI・機械学習" },
    { slug: "rag-llm", name: "RAG・LLM" },
    { slug: "data-visualization", name: "データ可視化" },
  ],
};
